import { useState } from 'react';

import {
  Button,
  Cards,
  FormField,
  Header,
  Input,
  Modal,
  SegmentedControl,
  SpaceBetween,
} from '@cloudscape-design/components';

import { createItem, updateItem } from '../api/generic';
import ErrorBar from '../components/ErrorBar';
import UploadImage from '../components/UploadImage';
import { useAlertContext } from '../contexts/AlertContext';
import useSubscribe from '../hooks/useSubscribe';
import Tenant from '../models/Tenant';
import { sortName } from '../utils/sort';
import { toCamelCase } from './Tenants.logic';

const Tenants = () => {
  const alert = useAlertContext();
  const [items, setItems] = useState<Tenant[]>([]);
  const [error, setError] = useState<Error>();
  const [backfillingTenant, setBackfillingTenant] = useState<string | null>(null);

  const [departmentsInput, setDepartmentsInput] = useState('');

  const [editingTenantId, setEditingTenantId] = useState<string | null>(null);
  const [editedDepartments, setEditedDepartments] = useState<string>('');

  // Tier editing state (inline per card)
  const [editingTierTenantId, setEditingTierTenantId] = useState<string | null>(null);
  const [editedTier, setEditedTier] = useState<string>('');

  const [isModalVisible, setIsModalVisible] = useState(false);

  const [tenantName, setTenantName] = useState('');
  const [tenantTier, setTenantTier] = useState<string>('1'); // default tier on create

  useSubscribe<Tenant>('tenant', setItems, setError, sortName);

  const handleCreateTenant = async () => {
    try {
      const tierNum = Number.parseInt(tenantTier, 10);
      const safeTier = Number.isFinite(tierNum) ? Math.min(3, Math.max(1, tierNum)) : 1;

      await createItem<Tenant>('tenant', {
        name: toCamelCase(tenantName),
        displayName: tenantName,
        tier: safeTier,
        // departments: departmentsInput
        //   .split(',')
        //   .map((d) => d.trim())
        //   .filter((d) => d.length > 0),
      });

      setIsModalVisible(false);
      setTenantName('');
      setTenantTier('1');
      setDepartmentsInput('');
    } catch (e) {
      setError(e as Error);
    }
  };

  const handleBackfill = async (tenantName: string) => {
    setBackfillingTenant(tenantName);
    try {
      const response = await fetch('https://backfillstorefortenant-ogbfqlrosa-ew.a.run.app', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenantId: tenantName }),
      });

      if (!response.ok) {
        throw new Error(`Backfill failed: ${response.statusText}`);
      }

      alert.info(`Backfill triggered for ${tenantName}`);
    } catch (err) {
      console.error(err);
      setError(err as Error);
    } finally {
      setBackfillingTenant(null);
    }
  };

  return (
    <SpaceBetween size="l">
      <ErrorBar error={error} setError={setError} />
      <Button onClick={() => setIsModalVisible(true)}>Create New Tenant</Button>
      <Cards
        cardsPerRow={[{ cards: 2 }]}
        items={items}
        cardDefinition={{
          header: (item) => <Header variant="h2">{item.displayName}</Header>,
          sections: [
            {
              id: 'type',
              content: (item) => (
                <div style={{ marginTop: 16 }}>
                  <strong>Id:</strong> {item.id}
                </div>
              ),
            },
            {
              id: 'name',
              content: (item) => (
                <div>
                  <strong>Token:</strong> {item.name}
                </div>
              ),
            },
            {
              id: 'tier',
              content: (item) => {
                const isEditing = editingTierTenantId === item.id;
                const currentTier = String(item?.tier ?? 1);

                return (
                  <>
                    <FormField label="Tier (1–3)">
                      <SegmentedControl
                        selectedId={isEditing ? editedTier || currentTier : currentTier}
                        onChange={async ({ detail }) => {
                          if (detail.selectedId === 'save') {
                            await updateItem('tenant', item.id, {
                              tier: Number.parseInt(editedTier, 10),
                            });
                            setEditingTierTenantId(null);
                          } else if (detail.selectedId === 'edit' && !isEditing) {
                            setEditingTierTenantId(item.id);
                            setEditedTier(currentTier);
                          } else {
                            setEditedTier(detail.selectedId);
                          }
                        }}
                        options={[
                          { id: '1', text: '1', disabled: !isEditing },
                          { id: '2', text: '2', disabled: !isEditing },
                          { id: '3', text: '3', disabled: !isEditing },
                          { id: !isEditing ? 'edit' : 'save', iconName: !isEditing ? 'edit' : 'check' },
                        ]}
                      />
                    </FormField>
                  </>
                );
              },
            },
            {
              id: 'logo',
              content: (item) => (
                <div>
                  <UploadImage key={'ri_upload'} fileName="image" filePath={'logos/' + item.name} />
                </div>
              ),
            },
            {
              id: 'backfill',
              content: (item) => (
                <div style={{ marginTop: 12 }}>
                  <Button
                    onClick={() => handleBackfill(item.name)}
                    loading={backfillingTenant === item.name}
                    disabled={backfillingTenant !== null || (item?.tier ?? 1) < 3}
                  >
                    Backfill Store
                  </Button>
                </div>
              ),
            },
            {
              id: 'departments',
              content: (item) =>
                editingTenantId === item.id ? (
                  <SpaceBetween size="xs" direction="vertical">
                    <Input value={editedDepartments} onChange={(e) => setEditedDepartments(e.detail.value)} />
                    <SpaceBetween size="xs" direction="horizontal">
                      <Button
                        variant="primary"
                        onClick={async () => {
                          const parsed = editedDepartments
                            .split(',')
                            .map((d) => d.trim())
                            .filter((d) => d.length > 0);
                          await updateItem('tenant', item.id, {
                            departments: parsed,
                          });
                          setEditingTenantId(null);
                          setEditedDepartments('');
                        }}
                      >
                        Save
                      </Button>
                      <Button
                        onClick={() => {
                          setEditingTenantId(null);
                          setEditedDepartments('');
                        }}
                      >
                        Cancel
                      </Button>
                    </SpaceBetween>
                  </SpaceBetween>
                ) : (
                  <div>
                    <strong>Departments:</strong>{' '}
                    {item.departments && item.departments.length > 0 && typeof item.departments[0] === 'string'
                      ? item.departments.join(', ')
                      : item.departments && item.departments.length > 0
                        ? item.departments.map((d: any) => d.name).join(', ')
                        : 'None'}
                    <div style={{ marginTop: 8 }}>
                      <Button
                        iconName="edit"
                        variant="inline-link"
                        onClick={() => {
                          setEditingTenantId(item.id);
                          setEditedDepartments((item.departments ?? []).join(', '));
                        }}
                      >
                        Edit
                      </Button>
                    </div>
                  </div>
                ),
            },
          ],
        }}
      />

      <Modal
        visible={isModalVisible}
        header="Create New Tenant"
        footer={
          <SpaceBetween direction="horizontal" size="xs">
            <Button variant="link" onClick={() => setIsModalVisible(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleCreateTenant}>
              Create
            </Button>
          </SpaceBetween>
        }
        onDismiss={() => setIsModalVisible(false)}
      >
        <SpaceBetween size="m">
          <FormField label="Tenant Name">
            {toCamelCase(tenantName)}
            <Input value={tenantName} onChange={(event) => setTenantName(event.detail.value)} />
          </FormField>

          <FormField label="Tier (1–3)">
            <SegmentedControl
              selectedId={tenantTier}
              onChange={({ detail }) => setTenantTier(detail.selectedId)}
              options={[
                { id: '1', text: '1' },
                { id: '2', text: '2' },
                { id: '3', text: '3' },
              ]}
            />
          </FormField>

          <FormField label="Departments (comma-separated)">
            <Input value={departmentsInput} onChange={(event) => setDepartmentsInput(event.detail.value)} />
          </FormField>
        </SpaceBetween>
      </Modal>
    </SpaceBetween>
  );
};

export default Tenants;
